import React, { createContext, useContext } from 'react';
import type { SettingsTabValue, TabValue } from './types';

/**
 * Admin context value: state and handlers for the admin page and all subcomponents.
 * Typed loosely so tab components can access what they need without prop drilling.
 */
export interface AdminContextValue {
  // Routing / auth
  subreddit: string | null;
  canonicalSubredditName: string;
  user: { username: string; isModerator?: boolean; moderatorSubreddit?: string | null } | null;
  /** True when the signed-in user is a site admin (full panel access). */
  isSiteAdmin?: boolean;
  // Tabs
  activeTab: TabValue | null;
  setActiveTab: (v: TabValue | null) => void;
  activeSettingsTab: SettingsTabValue;
  setActiveSettingsTab: (v: SettingsTabValue) => void;
  // Message / dialogs
  message: { type: 'success' | 'error' | 'warning'; text: string; details?: string } | null;
  setMessage: (m: { type: 'success' | 'error' | 'warning'; text: string; details?: string } | null) => void;
  clearDataDialogOpen: boolean;
  setClearDataDialogOpen: (v: boolean) => void;
  isClearingData: boolean;
  // Allow any other state/handlers used by tab components
  [key: string]: any;
}

const AdminContext = createContext<AdminContextValue | null>(null);

export function useAdminContext(): AdminContextValue {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('Admin components must be used inside AdminContext.Provider');
  return ctx;
}

export function AdminContextProvider({
  value,
  children,
}: {
  value: AdminContextValue;
  children: React.ReactNode;
}) {
  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}
